import { motion } from "framer-motion";
import {
  PiggyBank, Calendar, Target, Sunset, GraduationCap,
  Receipt, Building, Crown, ClipboardCheck, ArrowUpRight,
} from "lucide-react";

const SERVICES = [
  { icon: PiggyBank, title: "Mutual Funds", desc: "Curated equity, debt and hybrid funds aligned to your risk profile and time-horizon." },
  { icon: Calendar, title: "SIP Planning", desc: "Disciplined systematic investments designed to compound wealth across market cycles." },
  { icon: Target, title: "Goal-Based Investing", desc: "Investments engineered around specific life milestones — not arbitrary returns." },
  { icon: Sunset, title: "Retirement Planning", desc: "Build a resilient corpus and predictable post-retirement income stream." },
  { icon: GraduationCap, title: "Children's Education", desc: "Inflation-aware planning for higher education in India and abroad." },
  { icon: Receipt, title: "Tax Saving (ELSS)", desc: "Section 80C compliant equity-linked schemes for tax-efficient wealth creation." },
  { icon: Building, title: "Corporate Treasury", desc: "Liquidity-led debt solutions for businesses, trusts and institutions." },
  { icon: Crown, title: "HNI Portfolio Services", desc: "Bespoke multi-asset construction and quarterly portfolio reviews for HNI clients." },
  { icon: ClipboardCheck, title: "Portfolio Review", desc: "Independent diagnostics of your existing holdings — risk, overlap, and opportunity." },
];

export default function Services() {
  return (
    <section id="services" data-testid="services-section" className="section relative bg-[#080808]">
      <div className="absolute inset-0 bg-grid opacity-20 pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-14">
          <div>
            <p className="text-xs tracking-[0.35em] uppercase text-[#D4AF37]">Our Services</p>
            <h2 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl text-white mt-4 leading-[1.05] tracking-tight max-w-3xl">
              A full-stack wealth platform.
              <span className="text-gold-gradient"> Engineered around you.</span>
            </h2>
          </div>
          <p className="max-w-md text-gray-400 leading-relaxed">
            From your first SIP to multi-generational wealth planning — every offering is research-backed and
            delivered with institutional discipline.
          </p>
        </div>

        <div id="solutions" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {SERVICES.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: (i % 3) * 0.08 }}
                data-testid={`service-card-${i}`}
                className="shimmer group relative rounded-2xl bg-[#111111] border border-white/8 p-7 hover:border-[#D4AF37]/50 hover:-translate-y-1 transition-all duration-500"
              >
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#D4AF37]/20 to-[#D4AF37]/5 border border-[#D4AF37]/30 flex items-center justify-center text-[#D4AF37] mb-5">
                  <Icon size={22} />
                </div>
                <h3 className="font-display font-semibold text-xl text-white">{s.title}</h3>
                <p className="text-gray-400 text-sm mt-2 leading-relaxed">{s.desc}</p>
                <div className="mt-6 flex items-center gap-2 text-xs text-[#D4AF37] uppercase tracking-[0.25em] opacity-0 group-hover:opacity-100 transition">
                  Learn more <ArrowUpRight size={14} />
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
